<template>
  <v-container fluid>
    <report-title :title="options.title" class="mb-2" />
    <report-sub-category :tabs="options.tabs" @select-tab="onTabSelect" />
    <v-form ref="form" v-model="valid" class="mb-4 mt-4">
      <v-row justify="space-around" class="px-0">
        <v-col cols="4" v-if="options.organizations && options.organizations.length">
          <v-autocomplete
            v-model="organizationId"
            :items="options.organizations"
            item-text="nameRu"
            item-value="id"
            dense
            filled
            :rules="[v => v != null || 'Выберите организацию']"
            label="Организация"
          />
        </v-col>
        <v-col cols="3">
          <report-date-picker :rangeValue="range.start" />
        </v-col>
        <v-col cols="2">
          <report-date-picker :rangeValue="range.end" />
        </v-col>
        <v-col cols="2" align="center">
          <v-btn-toggle>
            <v-btn
              rounded
              color="primary"
              dark
              :loading="loading"
              @click="loadReport"
            >
              Показать отчет
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
    </v-form>
    <report-kpi-bar />
    <report-chart :chartOptions="chartOptions" :series="series" />
    <report-alert />
    <v-row>
      <v-col>
        <v-data-table
          :headers="options.headers"
          :items="options.data"
          :loading="loading"
          class="elevation-1"
          :server-items-length="options.allCount"
          :options.sync="tableOptions"
          :footer-props="{
            itemsPerPageOptions: [5, 10, 15, 50],
            itemsPerPageAllText: 'Все',
            itemsPerPageText: 'Строк на страницу',
            showFirstLastPage: true
          }"
        >
          <template v-slot:progress>
            <v-progress-linear color="primary" indeterminate />
          </template>
          <template v-slot:[`footer.page-text`]="items">
            {{ items.pageStart }} - {{ items.pageStop }} из {{ options.allCount || items.itemsLength }}
          </template>
          <template v-slot:item.dateTime="{ item }">
            <td>{{ item.dateTime | date }}</td>
          </template>
          <template v-slot:item.regDateTime="{ item }">
            <td>{{ item.regDateTime | date }}</td>
          </template>
          <template v-slot:item.temperature="{ item }">
            {{ item.temperature != null ? item.temperature.toFixed(2) : '—' }}
          </template>
          <template v-slot:item.volume="{ item }">
            {{ item.volume != null ? item.volume.toFixed(2) : '—' }}
          </template>
          <template v-slot:item.pressure="{ item }">
            {{ item.pressure != null ? item.pressure.toFixed(2) : '—' }}
          </template>
          <template v-slot:no-data>
            <div class="text-center pa-4 grey--text">
              По выбранным фильтрам данных не найдено. Задайте период и организацию и нажмите «Показать отчет».
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { Vue } from "vue-property-decorator";
import { mapActions, mapState } from "vuex";
import { format, startOfYear } from "date-fns";

export default Vue.extend({
  name: "ReportGasTransport",
  components: {
    ReportTitle: () => import("@/components/report/ReportTitle.vue"),
    ReportSubCategory: () => import("@/components/report/ReportSubCategory.vue"),
    ReportDatePicker: () => import("@/components/report/ReportDatePicker.vue"),
    ReportKpiBar: () => import("@/components/report/ReportKPIBar.vue"),
    ReportChart: () => import("@/components/report/ReportChart.vue"),
    ReportAlert: () => import("@/components/report/ReportAlert.vue")
  },
  data() {
    return {
      valid: true,
      organizationId: 0,
      tableOptions: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
      gasReportReady: false,
      selectedOperationTypeId: null
    };
  },
  computed: {
    ...mapState("report", ["range", "loading", "chartOptions", "series"]),
    options() {
      return this.$store.state.report.optionsGasTransport;
    }
  },
  watch: {
    tableOptions: {
      handler() {
        if (!this.gasReportReady) return;
        if (!this.range?.start?.value || !this.range?.end?.value) return;
        this.loadReport(false);
      },
      deep: true
    }
  },
  methods: {
    ...mapActions("report", [
      "fetchGasTransportOrganizations",
      "fetchGasTransportData",
      "resetGasTransportReport"
    ]),
    onTabSelect(tab) {
      this.selectedOperationTypeId = tab.operationTypeId;
      this.resetGasTransportReport();
      const dateFrom = format(startOfYear(Date.now()), "yyyy-MM-dd");
      const dateTo = format(Date.now(), "yyyy-MM-dd");
      this.$store.commit("report/RANGE", { start: dateFrom, end: dateTo });
      this.tableOptions = { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] };
      this.fetchGasTransportData({
        path: "",
        params: {
          organizationId: this.organizationId,
          date_from: dateFrom,
          date_to: dateTo,
          operationTypeId: this.selectedOperationTypeId,
          page: 1,
          pageSize: 10
        }
      });
    },
    async loadReport(resetPage = true) {
      if (this.$refs.form && !this.$refs.form.validate()) return;
      const start = this.range.start.value;
      const end = this.range.end.value;
      if (!start || !end) return;
      if (resetPage) this.tableOptions.page = 1;
      const page = this.tableOptions.page;
      const pageSize = this.tableOptions.itemsPerPage;
      const params = {
        organizationId: this.organizationId,
        date_from: start,
        date_to: end,
        operationTypeId: this.selectedOperationTypeId,
        page,
        pageSize
      };
      await this.fetchGasTransportData({ path: "", params });
    }
  },
  async mounted() {
    const dateFrom = format(startOfYear(Date.now()), "yyyy-MM-dd");
    const dateTo = format(Date.now(), "yyyy-MM-dd");
    await this.fetchGasTransportOrganizations();
    this.resetGasTransportReport();
    this.$store.commit("report/RANGE", { start: dateFrom, end: dateTo });
    const firstTab = this.options.tabs[0];
    this.selectedOperationTypeId = firstTab.operationTypeId;
    this.$store.commit("report/CURRENT_TAB", firstTab);
    this.tableOptions = { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] };
    await this.fetchGasTransportData({
      path: "",
      params: {
        organizationId: 0,
        date_from: dateFrom,
        date_to: dateTo,
        operationTypeId: this.selectedOperationTypeId,
        page: 1,
        pageSize: 10
      }
    });
    this.gasReportReady = true;
  }
});
</script>
